import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EmptyError, firstValueFrom } from 'rxjs';

@Injectable()
export class BlobCacheService implements OnDestroy {

    // <fileUrl, blobUrl>
    private blobUrlCache: Map<string, string> = new Map();

    constructor(
        private http: HttpClient,
        private destroyRef: DestroyRef) {
    }

    ngOnDestroy(): void {
        this.disposeAll();
    }

    public async getBlobUrl(fileUrl: string, forceFresh: boolean = false): Promise<string> {
        if (this.blobUrlCache.has(fileUrl)) {
            if (!forceFresh) {
                return this.blobUrlCache.get(fileUrl);
            }

            this.disposeBlobUrl(fileUrl);
        }

        let blob: Blob;

        try {
            blob = await firstValueFrom(
                this.http.get(fileUrl, { responseType: 'blob' })
                    .pipe(takeUntilDestroyed(this.destroyRef))
            );
        }
        catch (error) {
            // EmptyError is thrown if service is destroyed while request is in progress and is canceled.
            // If that happens return empty string.
            if (error instanceof EmptyError) {
                return '';
            }

            throw error;
        }

        const blobUrl = URL.createObjectURL(blob);

        this.blobUrlCache.set(fileUrl, blobUrl);

        return blobUrl;
    }

    public isBlobUrlCached(fileUrl: string): boolean {
        return this.blobUrlCache.has(fileUrl);
    }

    public disposeBlobUrl(fileOrBlobUrl: string): void {
        if (this.blobUrlCache.has(fileOrBlobUrl)) {
            const blobUrl = this.blobUrlCache.get(fileOrBlobUrl);

            URL.revokeObjectURL(blobUrl);

            this.blobUrlCache.delete(fileOrBlobUrl);
        }
        else {
            URL.revokeObjectURL(fileOrBlobUrl);
        }
    }

    public disposeAll(): void {
        for (const [_, value] of this.blobUrlCache) {
            URL.revokeObjectURL(value);
        }

        this.blobUrlCache.clear();
    }
}
